import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Download, ChevronDown, ChevronUp, TrendingUp, TrendingDown, EyeOff, ListChecks, MessageCircle, Lightbulb, ArrowRight } from 'lucide-react';
// Mock Data for the AI Insights
const insightsData = [{
  id: 'strengths',
  title: 'Leadership Strengths',
  icon: TrendingUp,
  iconColor: 'text-emerald-500',
  bgColor: 'bg-emerald-100',
  content: <div className="space-y-4" data-id="element-362" data-mp="2tvcc981plowl">
        <p className="text-sm text-slate-600" data-id="element-363" data-mp="2tuko2iafytgc">
          Based on 360° feedback, Jane demonstrates exceptional capabilities in
          the following areas:
        </p>
        <ul className="space-y-3" data-id="element-364" data-mp="33tiexrvcaz2i">
          <li className="flex items-start" data-id="element-365" data-mp="33sh15fmxvyh3">
            <div className="mt-0.5 mr-3 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-366" data-mp="33r5xrgxeosqi" />
            <div data-id="element-367" data-mp="14iinwtzosf6v">
              <strong className="text-sm font-medium text-slate-900" data-id="element-368" data-mp="19fhfp5vvoa1w">
                Strategic Execution (4.8/5.0)
              </strong>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-369" data-mp="2idxfj9ztbsxa">
                Consistently translates high-level vision into actionable,
                measurable team goals.
              </p>
            </div>
          </li>
          <li className="flex items-start" data-id="element-370" data-mp="28nm3t19w0ffu">
            <div className="mt-0.5 mr-3 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-371" data-mp="24lr2hwryi222" />
            <div data-id="element-372" data-mp="31brpprcogah9">
              <strong className="text-sm font-medium text-slate-900" data-id="element-373" data-mp="31c9oovqq52sv">
                Crisis Management (4.6/5.0)
              </strong>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-374" data-mp="2wd6kfdikj0ly">
                Maintains composure and provides clear direction during
                high-pressure situations.
              </p>
            </div>
          </li>
        </ul>
      </div>
}, {
  id: 'opportunities',
  title: 'Development Opportunities',
  icon: TrendingDown,
  iconColor: 'text-amber-500',
  bgColor: 'bg-amber-100',
  content: <div className="space-y-4" data-id="element-375" data-mp="1bz68spo0ddvc">
        <p className="text-sm text-slate-600" data-id="element-376" data-mp="1ddph278p4ywu">
          The data highlights specific areas where targeted development could
          yield significant performance improvements:
        </p>
        <ul className="space-y-3" data-id="element-377" data-mp="14du0fmwdgbpf">
          <li className="flex items-start" data-id="element-378" data-mp="14crb3jep6hbm">
            <div className="mt-0.5 mr-3 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-379" data-mp="18ew1m99umxu1" />
            <div data-id="element-380" data-mp="18gqq1xkbx82v">
              <strong className="text-sm font-medium text-slate-900" data-id="element-381" data-mp="unr9ds8t97tq">
                Cross-functional Communication (3.1/5.0)
              </strong>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-382" data-mp="zfslne5zayjr">
                Feedback indicates a need for more proactive information sharing
                with adjacent departments.
              </p>
            </div>
          </li>
          <li className="flex items-start" data-id="element-383" data-mp="bon2oq4qrq58">
            <div className="mt-0.5 mr-3 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-384" data-mp="bpoif7wpd0ta" />
            <div data-id="element-385" data-mp="gnzsnjqwtdc1">
              <strong className="text-sm font-medium text-slate-900" data-id="element-386" data-mp="gmexpt0onsdn">
                Delegation (3.4/5.0)
              </strong>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-387" data-mp="3t95fjj26l25m">
                Tendency to hold onto operational tasks rather than empowering
                direct reports.
              </p>
            </div>
          </li>
        </ul>
      </div>
}, {
  id: 'blind-spots',
  title: 'Blind Spot Analysis',
  icon: EyeOff,
  iconColor: 'text-rose-500',
  bgColor: 'bg-rose-100',
  content: <div className="space-y-4" data-id="element-388" data-mp="b1peuidjah9o">
        <p className="text-sm text-slate-600" data-id="element-389" data-mp="b2hatub1mh0h">
          A blind spot occurs when self-assessment significantly diverges from
          the assessment of others (gap &gt; 1.0).
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-390" data-mp="kqz9a4vuoc0y">
          <div className="flex justify-between items-center mb-3" data-id="element-391" data-mp="yra057x5tqm4">
            <span className="text-sm font-semibold text-slate-900" data-id="element-392" data-mp="yrjoys6gxrq8">
              Active Listening
            </span>
            <span className="text-xs font-medium bg-rose-100 text-rose-700 px-2 py-1 rounded-full" data-id="element-393" data-mp="tqch6t4bssjk">
              1.4 Gap
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-394" data-mp="2t67difwvfk2i">
            <div data-id="element-395" data-mp="2xygyrin1jvyg">
              <span className="text-slate-500 block mb-1" data-id="element-396" data-mp="2xxytjwiei7ee">Self Rating</span>
              <span className="font-medium text-slate-900" data-id="element-397" data-mp="31zsp5qdqol7p">4.5 (High)</span>
            </div>
            <div data-id="element-398" data-mp="259jhniukg57a">
              <span className="text-slate-500 block mb-1" data-id="element-399" data-mp="25a2z6ygb0rks">Others Rating</span>
              <span className="font-medium text-slate-900" data-id="element-400" data-mp="2a8mn34egkdfs">3.1 (Moderate)</span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-200 text-sm text-slate-600" data-id="element-401" data-mp="2eahlo1508cpv">
            <strong data-id="element-402" data-mp="2j9b13z3uqdso">AI Insight:</strong> You perceive yourself as highly
            empathetic and attentive, but peers report feeling rushed or unheard
            during 1:1 meetings.
          </div>
        </div>
      </div>
}, {
  id: 'action-plan',
  title: 'Recommended Action Plan',
  icon: ListChecks,
  iconColor: 'text-indigo-500',
  bgColor: 'bg-indigo-100',
  content: <div className="space-y-4" data-id="element-403" data-mp="3nxiwhoc509v">
        <p className="text-sm text-slate-600" data-id="element-404" data-mp="3oywasstpyur">
          Suggested immediate steps to address the identified development areas
          and blind spots:
        </p>
        <div className="space-y-3" data-id="element-405" data-mp="3r19o5yvcgoyd">
          {['Implement a "listen-first" rule in 1:1s: spend the first 10 minutes asking questions before offering solutions.', 'Identify two operational tasks this week to fully delegate to a direct report.', 'Schedule a monthly alignment sync with the Marketing and Sales directors to improve cross-functional flow.'].map((action, i) => <div key={i} className="flex items-start bg-white border border-slate-200 rounded-lg p-3 shadow-sm" data-id="element-406" data-mp="382xabzthmqwg" data-mpi={i}>
              <div className="flex items-center justify-center w-6 h-6 rounded bg-indigo-50 text-indigo-600 font-semibold text-xs mr-3 shrink-0" data-id="element-407" data-mp="1aa1i09tpmlp7">
                {i + 1}
              </div>
              <p className="text-sm text-slate-700 mt-0.5" data-id="element-408" data-mp="2zrewgv5dv3lo">{action}</p>
            </div>)}
        </div>
      </div>
}, {
  id: 'coaching',
  title: 'Suggested Coaching Topics',
  icon: MessageCircle,
  iconColor: 'text-blue-500',
  bgColor: 'bg-blue-100',
  content: <div className="space-y-4" data-id="element-409" data-mp="2usc3i92ognu2">
        <p className="text-sm text-slate-600" data-id="element-410" data-mp="3ijhhsjsyz829">
          Bring these topics to your next session with your executive coach or
          manager:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-411" data-mp="3ngzrwdiq3tbu">
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-id="element-412" data-mp="39i9lg7a0khat">
            <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center" data-id="element-413" data-mp="3ehw5azkckvsl">
              <Lightbulb className="w-4 h-4 text-blue-500 mr-2" data-id="element-414" data-mp="1y4fkkxjrpgfo" />
              The "Letting Go" Threshold
            </h4>
            <p className="text-xs text-slate-600" data-id="element-415" data-mp="234a7kq0f9lvg">
              Explore the psychological barriers preventing effective
              delegation. What feels risky about handing over control?
            </p>
          </div>
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-id="element-416" data-mp="1qk3gelativ5f">
            <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center" data-id="element-417" data-mp="24jcvfhnafj7t">
              <Lightbulb className="w-4 h-4 text-blue-500 mr-2" data-id="element-418" data-mp="1zl1l75t2z6p2" />
              Perception vs. Intent
            </h4>
            <p className="text-xs text-slate-600" data-id="element-419" data-mp="1bwidardy0vxi">
              Discuss the gap in Active Listening. How can you signal attention
              and empathy more visibly to your team?
            </p>
          </div>
        </div>
      </div>
}];
export function AIInsights() {
  // Default first two sections expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['strengths', 'opportunities']));
  const toggleSection = (id: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSections(newExpanded);
  };
  const expandAll = () => {
    setExpandedSections(new Set(insightsData.map(s => s.id)));
  };
  const collapseAll = () => {
    setExpandedSections(new Set());
  };
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-420" data-mp="hju31pdhh6k0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-421" data-mp="llyx2p7ug576">
        <div data-id="element-422" data-mp="qksayir1ly7v">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center" data-id="element-423" data-mp="qka7b121whpx">
            AI Insights
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-424" data-mp="vaz1io547ssr">
              Beta
            </span>
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-425" data-mp="15a80xwir1zmi">
            Machine learning analysis of Q3 Leadership Assessment patterns.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-426" data-mp="19bjhlcjnbaik">
          <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center bg-white" data-id="element-427" data-mp="2tt2gurkwor9t">
            <Download className="h-4 w-4 mr-2" data-id="element-428" data-mp="2pttjzdukkda1" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-6 shadow-sm" data-id="element-429" data-mp="2fuckje5wuvmi">
        <div className="flex items-center mb-4" data-id="element-430" data-mp="3dg06t68njxkb">
          <div className="bg-indigo-100 p-2 rounded-lg mr-3" data-id="element-431" data-mp="38igq7zohf8p7">
            <Sparkles className="h-5 w-5 text-indigo-600" data-id="element-432" data-mp="38j07rfa7zv2p" />
          </div>
          <h2 className="text-lg font-semibold text-indigo-900" data-id="element-433" data-mp="33q74kvuegusq">
            Executive Summary
          </h2>
        </div>
        <div className="space-y-4 text-indigo-900/80 text-sm leading-relaxed" data-id="element-434" data-mp="2ys5bdi39ff3j">
          <p data-id="element-435" data-mp="36r90fw2jln">
            Jane Doe's Q3 assessment reveals a highly capable leader who excels
            at <strong data-id="element-436" data-mp="3rgaww725184f">Strategic Execution</strong> and{' '}
            <strong data-id="element-437" data-mp="1srnkyzhce030">Crisis Management</strong>. Her overall score of 4.1/5.0
            places her in the top quartile of directors within the organization.
          </p>
          <p data-id="element-438" data-mp="1nux80l0u3klt">
            However, the AI model has detected a recurring pattern across peer
            and direct report feedback indicating friction in{' '}
            <strong data-id="element-439" data-mp="1xpyuf93q68kp">Cross-functional Communication</strong> and{' '}
            <strong data-id="element-440" data-mp="2bqi2uhc3rgne">Active Listening</strong>. There is a notable blind spot
            where Jane rates her empathy significantly higher than her team
            does. Addressing these interpersonal dynamics through targeted
            coaching and delegation will be critical for her transition to a
            VP-level role.
          </p>
        </div>
      </div>

      {/* Expandable Sections Controls */}
      <div className="flex justify-between items-center pt-4" data-id="element-441" data-mp="2ugre0gpvair8">
        <h3 className="text-lg font-semibold text-slate-900" data-id="element-442" data-mp="2uewqcun7ocjg">
          Detailed Analysis
        </h3>
        <div className="flex space-x-4 text-sm" data-id="element-443" data-mp="2qduqcblgzwgf">
          <button onClick={expandAll} className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors" data-id="element-444" data-mp="39517a5ym0auf">
            Expand All
          </button>
          <span className="text-slate-300" data-id="element-445" data-mp="3028etzdoxt30">|</span>
          <button onClick={collapseAll} className="text-slate-500 hover:text-slate-700 font-medium transition-colors" data-id="element-446" data-mp="305megzuyzo6r">
            Collapse All
          </button>
        </div>
      </div>

      {/* Expandable Sections */}
      <div className="space-y-4" data-id="element-447" data-mp="2v4yq6ly4llg8">
        {insightsData.map(section => {
        const isExpanded = expandedSections.has(section.id);
        const Icon = section.icon;
        return <div key={section.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-448" data-mp="2lf6tnm6gui94">
              <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors focus:outline-none" data-id="element-449" data-mp="2cdfqop3b8piy">
                <div className="flex items-center" data-id="element-450" data-mp="d6ike8ly53fh">
                  <div className={`p-2 rounded-lg mr-4 ${section.bgColor}`} data-id="element-451" data-mp="d4xn4b88z6dx">
                    <Icon className={`h-5 w-5 ${section.iconColor}`} data-id="element-452" data-mp="95f27fgfevb3" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900" data-id="element-453" data-mp="47nbupvfjh90">
                    {section.title}
                  </h3>
                </div>
                <div className="text-slate-400" data-id="element-454" data-mp="wu821n065naz">
                  {isExpanded ? <ChevronUp className="h-5 w-5" data-id="element-455" data-mp="rvoh9pxbahk7" /> : <ChevronDown className="h-5 w-5" data-id="element-456" data-mp="rxrdirp256xd" />}
                </div>
              </button>

              <AnimatePresence initial={false} data-id="element-457" data-mp="3clvy7g7czbsq">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-458" data-mp="3cjj5c802bh0w">
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100" data-id="element-459" data-mp="3g8bxbauvuddi">
                      {section.content}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>

      {/* Footer Call to Action */}
      <div className="mt-8 bg-slate-900 rounded-xl p-6 text-center shadow-sm" data-id="element-460" data-mp="3v5bcpkvz7aij">
        <h3 className="text-white font-semibold mb-2" data-id="element-461" data-mp="1vua8ysfbbqsx">Ready to take action?</h3>
        <p className="text-slate-400 text-sm mb-4 max-w-lg mx-auto" data-id="element-462" data-mp="20sbq37t6hg1r">
          Share these insights with your manager or executive coach to begin
          building your personalized development roadmap.
        </p>
        <button className="inline-flex items-center bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors" data-id="element-463" data-mp="3mbf70l4n893j">
          Schedule Coaching Sync
          <ArrowRight className="h-4 w-4 ml-2" data-id="element-464" data-mp="3vasitkg8p9rf" />
        </button>
      </div>
    </div>;
}